import React, { useState, useEffect } from 'react';
import {
  Bug,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Monitor,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader,
  X,
  Eye,
  Code
} from 'lucide-react';
import { formatDateInUserTimezone } from '../../utils/timezone';

const AdminErrorLogs = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('all');
  const [selectedError, setSelectedError] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchErrors = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(errorTypeFilter !== 'all' && { error_type: errorTypeFilter }),
        ...(resolvedFilter !== 'all' && { resolved: resolvedFilter === 'resolved' })
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/application-errors?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setErrors(data.errors);
        setTotalPages(data.pages);
      } else {
        setError('Failed to fetch application errors');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const resolveError = async (errorId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/application-errors/${errorId}/resolve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchErrors();
        setShowErrorDetails(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to resolve error');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [page, errorTypeFilter, resolvedFilter]);

  const getErrorTypeBadge = (errorType) => {
    const badges = {
      'email_error': 'bg-red-100 text-red-800',
      'monitor_check_error': 'bg-orange-100 text-orange-800',
      'payment_error': 'bg-yellow-100 text-yellow-800',
      'database_error': 'bg-purple-100 text-purple-800',
      'auth_error': 'bg-blue-100 text-blue-800',
      'api_error': 'bg-gray-100 text-gray-800'
    };
    return badges[errorType] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return formatDateInUserTimezone(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50">
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-64"></div>
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Application Error Logs</h1>
          <p className="text-gray-600">Monitor and resolve application errors</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <select
                value={errorTypeFilter}
                onChange={(e) => setErrorTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              >
                <option value="all">All Error Types</option>
                <option value="email_error">Email Errors</option>
                <option value="monitor_check_error">Monitor Errors</option>
                <option value="payment_error">Payment Errors</option>
                <option value="database_error">Database Errors</option>
                <option value="auth_error">Auth Errors</option>
                <option value="api_error">API Errors</option>
              </select>

              <select
                value={resolvedFilter}
                onChange={(e) => setResolvedFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              >
                <option value="all">All Status</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <span className="text-sm text-gray-600">
              {errors.length} errors
            </span>
          </div>
        </div>

        {/* Error Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Context
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {errors.map((errorItem) => (
                  <tr key={errorItem.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getErrorTypeBadge(errorItem.error_type)}`}>
                        {errorItem.error_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={errorItem.error_message}>
                        {errorItem.error_message}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        {errorItem.resolved ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-xs ${errorItem.resolved ? 'text-green-600' : 'text-red-600'}`}>
                          {errorItem.resolved ? 'Resolved' : 'Open'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(errorItem.timestamp)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {errorItem.user_id && (
                          <User className="w-4 h-4 text-blue-500" title="Has user context" />
                        )}
                        {errorItem.monitor_id && (
                          <Monitor className="w-4 h-4 text-green-500" title="Has monitor context" />
                        )}
                        {errorItem.stack_trace && (
                          <Code className="w-4 h-4 text-purple-500" title="Has stack trace" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => {
                            setSelectedError(errorItem);
                            setShowErrorDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!errorItem.resolved && (
                          <button
                            onClick={() => resolveError(errorItem.id)}
                            disabled={actionLoading}
                            className="text-green-600 hover:text-green-900 p-1.5 rounded hover:bg-green-50"
                            title="Mark as resolved"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{page}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      {showErrorDetails && selectedError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Error Details</h2>
                <button
                  onClick={() => setShowErrorDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Error Info */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Error Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Error Type</label>
                      <p className="text-gray-900 mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getErrorTypeBadge(selectedError.error_type)}`}>
                          {selectedError.error_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <div className="flex items-center mt-1">
                        {selectedError.resolved ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 mr-1" />
                        )}
                        <span className={selectedError.resolved ? 'text-green-600' : 'text-red-600'}>
                          {selectedError.resolved ? 'Resolved' : 'Open'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Timestamp</label>
                      <p className="text-gray-900 mt-1">{formatDate(selectedError.timestamp)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Error ID</label>
                      <p className="text-gray-900 mt-1 font-mono text-sm">{selectedError.id}</p>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Error Message</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">{selectedError.error_message}</p>
                  </div>
                </div>

                {/* Context */}
                {selectedError.context && Object.keys(selectedError.context).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Context Information</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <pre className="text-sm text-blue-800 whitespace-pre-wrap">
                        {JSON.stringify(selectedError.context, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Stack Trace */}
                {selectedError.stack_trace && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Stack Trace</h3>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-green-400 whitespace-pre-wrap">
                        {selectedError.stack_trace}
                      </pre>
                    </div>
                  </div>
                )}

                {/* User and Monitor Context */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedError.user_id && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">User Context</h4>
                      <p className="text-blue-700 font-mono text-sm">{selectedError.user_id}</p>
                    </div>
                  )}
                  {selectedError.monitor_id && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">Monitor Context</h4>
                      <p className="text-green-700 font-mono text-sm">{selectedError.monitor_id}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!selectedError.resolved && (
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => resolveError(selectedError.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {actionLoading ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      <span>Mark as Resolved</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrorLogs;